import { cn } from "@/lib/utils";
import useMediaQuery from "@/hooks/useMediaQuery";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/panel/ui";
import Card from "./Card";
// Direto do módulo, e não do barril `index.js`: ele exporta esta própria tabela,
// e importar de lá fecharia um ciclo.
import { Select } from "./Input";
import { useDensity } from "./density";

/**
 * Tabela das listagens do painel.
 *
 * Resolve três coisas que estavam soltas nas 25 telas com `<table>`:
 *
 * 1. ESTADOS. Só 16 de 41 telas mostravam carregando/erro, e 9 tinham vazio de
 *    verdade — o resto usava `<td colSpan={6}>Nenhum registro.</td>`, texto
 *    cinza sem saída. Aqui os três estados são obrigatórios por construção.
 * 2. SEMÂNTICA. Nenhum `<th>` tinha `scope`, então leitor de tela não associava
 *    célula a coluna.
 * 3. ALINHAMENTO DE NÚMERO. `mono` na coluna aplica `tabular-nums`, para a
 *    coluna de dinheiro não dançar entre as linhas.
 *
 * Uso:
 *   <DataTable
 *     rows={products} rowKey={(p) => p.id}
 *     loading={isLoading} error={isError} onRetry={refetch}
 *     empty={{ icon: Package, title: "Nenhum produto", desc: "...", action: <Button/> }}
 *     sort={sort} onSortChange={setSort}
 *     columns={[
 *       { key: "name", header: "Produto", sortable: true, cell: (p) => p.name },
 *       { key: "price", header: "Preço", align: "right", mono: true, sortable: true, cell: (p) => brl(p.price) },
 *     ]}
 *   />
 *
 * ORDENAÇÃO. `sortable: true` faz o cabeçalho virar botão e manda a `key` da
 * coluna ao servidor (`sortKey` sobrescreve). O ciclo do clique é crescente →
 * decrescente → padrão. Quem guarda o estado é o `usePaginatedList`, que já põe
 * `sort`/`dir` na query e volta para a página 1 — ordenar no cliente ordenaria
 * só as 25 linhas da página atual, respondendo a pergunta errada.
 *
 * ABAIXO DE `md` a mesma definição de colunas vira CARTÃO, sem scroll
 * horizontal. A convenção, que já vale nas telas migradas:
 *   - a primeira coluna titula o cartão (ou a marcada com `cardTitle: true`,
 *     que é o que histórico usa — senão o cartão sai intitulado pela data);
 *   - a coluna `acoes` (ou `role: "actions"`) vai para o rodapé;
 *   - as demais viram pares rótulo/valor;
 *   - `hideOnCard: true` omite a coluna no cartão.
 *
 * Foi feito assim porque a tabela de Clientes media 790px numa tela de 390px:
 * as três últimas colunas — inclusive a de Ações — ficavam fora do alcance, e
 * editar ou excluir exigia descobrir que havia scroll dentro do card.
 */

const ALIGN = { left: "text-left", center: "text-center", right: "text-right" };

/* A coluna diz que é ordenável com `sortable: true` (usa a própria `key` como
   chave enviada ao servidor) ou com `sortKey: "outra"`. Nada acontece se a tela
   não passar `onSortChange`: cabeçalho clicável que não ordena é pior que
   cabeçalho estático. */
const chaveDeOrdem = (c) => c.sortKey || c.key;
const ordenavel = (c) => !!(c.sortable || c.sortKey);

/**
 * Próximo estado ao clicar no cabeçalho: crescente → decrescente → sem ordem.
 *
 * O terceiro clique volta ao padrão do endpoint de propósito. Sem ele não há
 * caminho de volta para "mais recentes" a não ser recarregar a página — e
 * "mais recentes" é o que a tela mostra ao abrir, então some justamente a ordem
 * que a pessoa tinha antes de mexer.
 *
 * `sortDir: "desc"` na coluna inverte o primeiro clique, para colunas em que a
 * pergunta natural é pelo maior: data mais nova, venda maior, estoque mais alto.
 */
function proximaOrdenacao(coluna, atual) {
  const key = chaveDeOrdem(coluna);
  const primeira = coluna.sortDir === "desc" ? "desc" : "asc";
  if (!atual || atual.key !== key) return { key, dir: primeira };
  if (atual.dir === primeira) return { key, dir: primeira === "asc" ? "desc" : "asc" };
  return null;
}

/**
 * O indicador de ordem no cabeçalho.
 *
 * Par de triângulos cinzas quando a coluna não está ordenando, e um único
 * triângulo escuro no sentido ativo quando está. O par é o que ANUNCIA que a
 * coluna ordena: seta que só aparece depois do clique deixa a tabela parecer
 * estática, e ninguém clica no que não parece clicável.
 *
 * A caixa tem tamanho fixo porque o conteúdo troca de dois triângulos para um:
 * sem ela, o cabeçalho encolheria a cada clique e a linha inteira dançaria.
 *
 * `aria-hidden` porque quem lê a ordem em voz alta é o `aria-sort` do `<th>` —
 * aqui é desenho, e anunciá-lo de novo seria repetição.
 */
function SetaDeOrdem({ dir }) {
  const Triangulo = ({ d, aceso }) => (
    <svg viewBox="0 0 8 5" width="8" height="5" fill="currentColor"
      className={aceso ? "text-slate-700" : "text-slate-300"}>
      <path d={d} />
    </svg>
  );

  return (
    <span className="inline-flex flex-col items-center justify-center gap-px w-2 h-3 shrink-0" aria-hidden>
      {dir !== "desc" && <Triangulo d="M4 0 8 5H0z" aceso={dir === "asc"} />}
      {dir !== "asc" && <Triangulo d="M0 0h8L4 5z" aceso={dir === "desc"} />}
    </span>
  );
}

/* Rótulo do sentido. Número não se ordena de "A a Z": numa coluna de dinheiro a
   pessoa procura "o mais caro", e é assim que a opção precisa se chamar. `mono`
   já marca as colunas numéricas na definição da tabela. */
const SENTIDO = {
  texto: { asc: "A–Z", desc: "Z–A" },
  numero: { asc: "menor primeiro", desc: "maior primeiro" },
};

const DENSITY = {
  comfortable: { head: "px-4 py-3 first:pl-5 last:pr-5", cell: "px-4 py-3 first:pl-5 last:pr-5" },
  compact: { head: "px-3 py-2 first:pl-4 last:pr-4", cell: "px-3 py-1.5 first:pl-4 last:pr-4" },
};

export default function DataTable({
  columns,
  rows,
  rowKey,
  loading = false,
  error = false,
  onRetry,
  errorMessage,
  empty,
  onRowClick,
  rowClassName,
  rowTestId,
  density,
  caption,
  className,
  sort,
  onSortChange,
}) {
  // Sem prop, segue a preferência da pessoa (guardada no painel). A prop existe
  // para a tela que precisa fixar uma densidade — extrato de caixa, por exemplo.
  const preferred = useDensity();
  const d = DENSITY[density || preferred] || DENSITY.comfortable;
  /* Cartão OU tabela, nunca os dois no DOM. Com `md:hidden` os 25 cartões
     continuavam renderizados no desktop: o dobro de nós, os botões de ação
     duplicados e `data-testid` repetido em toda listagem.
     Fica antes dos returns condicionais — hook não pode ser condicional. */
  const wide = useMediaQuery("(min-width: 768px)");

  if (error) {
    /* `errorMessage` existe porque a tela sabe o que falhou melhor que o kit:
       "Não foi possível carregar suas notas fiscais" orienta mais que o texto
       genérico. Sem a prop, as telas migradas perdiam a mensagem própria. */
    return <Card className={className}><ErrorState onRetry={onRetry} message={errorMessage} /></Card>;
  }
  if (loading) {
    return <Card className={className}><TableSkeleton cols={columns.length} /></Card>;
  }
  if (!rows.length) {
    return (
      <Card className={className}>
        <EmptyState
          icon={empty?.icon}
          title={empty?.title || "Nenhum registro encontrado"}
          desc={empty?.desc}
          action={empty?.action}
          testId={empty?.testId}
        />
      </Card>
    );
  }

  const actionCol = columns.find((c) => c.role === "actions" || c.key === "acoes");
  const corpo = columns.filter((c) => c !== actionCol && !c.hideOnCard);

  /* Quem titula o cartão.
     A primeira coluna é um bom padrão para listagem de cadastro ("Produto",
     "Cliente"), e um mau padrão para HISTÓRICO: ali a primeira coluna é a data,
     e o cartão saía intitulado "26/08/2026 14:32" com o produto escondido entre
     os pares rótulo/valor. `cardTitle: true` deixa a tela dizer qual coluna
     nomeia a linha, sem mexer na ordem que o desktop precisa. */
  const escolhida = corpo.find((c) => c.cardTitle);
  const titleCol = escolhida || corpo[0];
  const restCols = corpo.filter((c) => c !== titleCol);

  /* Testid da linha. Sem `rowTestId` o kit emite `datatable-row`, mas a tela que
     já tinha um nome próprio precisa mantê-lo NO ELEMENTO DA LINHA: pendurar o
     testid numa célula quebra `within(getByTestId(linha))`, que é justamente
     como se alcança os botões de ação a partir da linha. */
  const testIdDe = (row, i) => (typeof rowTestId === "function" ? rowTestId(row, i) : rowTestId) || "datatable-row";

  if (!wide) {
    /* ---------- Cartões (abaixo de md) ---------- */
    /* O cartão não tem cabeçalho, então a ordenação por coluna sumiria no
       celular — que é onde o vendedor de balcão trabalha. As mesmas colunas
       ordenáveis viram uma lista aqui, com os dois sentidos explícitos: sem
       cabeçalho não há o clique-de-novo-para-inverter. */
    const ordenaveis = columns.filter(ordenavel);
    const seletor = onSortChange && ordenaveis.length > 0 && (
      <label className="flex items-center gap-2 px-1 text-sm text-slate-500">
        <span className="shrink-0">Ordenar por</span>
        <Select
          value={sort ? `${sort.key}:${sort.dir}` : ""}
          onChange={(e) => {
            const [key, dir] = e.target.value.split(":");
            onSortChange(key ? { key, dir } : null);
          }}
          data-testid="datatable-sort"
          className="w-auto px-2.5 py-1.5 text-slate-700"
        >
          <option value="">Padrão</option>
          {ordenaveis.flatMap((c) => {
            const sentido = c.mono ? SENTIDO.numero : SENTIDO.texto;
            return ["asc", "desc"].map((dir) => (
              <option key={`${chaveDeOrdem(c)}:${dir}`} value={`${chaveDeOrdem(c)}:${dir}`}>
                {c.header} · {sentido[dir]}
              </option>
            ));
          })}
        </Select>
      </label>
    );

    return (
      <div className={cn("space-y-3", className)}>
        {seletor}
        {rows.map((row, i) => {
          const clickable = !!onRowClick;
          const Wrapper = clickable ? "button" : "div";
          return (
            <Card
              key={rowKey ? rowKey(row) : i}
              data-testid={testIdDe(row, i)}
              className={cn("overflow-hidden", typeof rowClassName === "function" ? rowClassName(row) : rowClassName)}
            >
              <Wrapper
                {...(clickable ? { type: "button", onClick: () => onRowClick(row), "data-ring": "" } : {})}
                className={cn("w-full text-left p-4", clickable && "hover:bg-slate-50 transition-colors")}
              >
                {titleCol && (
                  <div className="font-medium text-slate-900">
                    {titleCol.cell ? titleCol.cell(row) : row[titleCol.key]}
                  </div>
                )}
                {restCols.length > 0 && (
                  <dl className="mt-3 space-y-1.5">
                    {restCols.map((c) => (
                      <div key={c.key} className="flex items-start justify-between gap-3 text-sm">
                        <dt className="text-slate-500 shrink-0">{c.header}</dt>
                        <dd className={cn("text-right min-w-0", c.mono && "font-mono tabular-nums", c.className)}>
                          {c.cell ? c.cell(row) : row[c.key]}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </Wrapper>
              {/* No toque os alvos sobem para 44px (WCAG 2.5.5): os mesmos botões
                  de ícone que servem ao mouse na tabela ficariam em 32px aqui.
                  Piso com `min-`, não tamanho fixo — com `w-11` cravado, o botão
                  que tem rótulo ("Devolução", "Consultar") era espremido em 44px
                  e o texto vazava. */}
              {actionCol && (
                <div className="px-3 py-2 border-t border-slate-100 bg-slate-50/60 flex flex-wrap items-center justify-end gap-1 [&_button]:min-h-11 [&_button]:min-w-11 [&_a]:min-h-11 [&_a]:min-w-11">
                  {actionCol.cell ? actionCol.cell(row) : null}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    );
  }

  /* ---------- Tabela (md e acima) ---------- */
  return (
    <Card className={cn("overflow-x-auto", className)}>
        <table className="w-full text-sm">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr className="border-b border-slate-200">
              {columns.map((c) => {
                const podeOrdenar = !!onSortChange && ordenavel(c);
                const ativa = podeOrdenar && sort?.key === chaveDeOrdem(c);
                return (
                  <th
                    key={c.key}
                    scope="col"
                    style={c.width ? { width: c.width } : undefined}
                    /* `aria-sort` é o que informa a ordem a quem usa leitor de
                       tela: sem ele, a seta é decoração invisível e a tabela
                       parece estar sempre na ordem original. */
                    aria-sort={ativa ? (sort.dir === "asc" ? "ascending" : "descending") : podeOrdenar ? "none" : undefined}
                    className={cn("text-eyebrow uppercase text-slate-500 whitespace-nowrap", d.head, ALIGN[c.align] || ALIGN.left)}
                  >
                    {podeOrdenar ? (
                      <button
                        type="button"
                        onClick={() => onSortChange(proximaOrdenacao(c, sort))}
                        data-testid={`sort-${chaveDeOrdem(c)}`}
                        data-ring=""
                        /* O texto do botão herda o alinhamento da célula, mas a
                           CAIXA precisa acompanhar: numa coluna à direita um
                           botão de largura total deixaria a seta longe do rótulo. */
                        className={cn(
                          "inline-flex items-center gap-1 uppercase rounded hover:text-slate-800 transition-colors",
                          ativa && "text-slate-800",
                        )}
                        title={ativa ? "Trocar o sentido ou voltar à ordem padrão" : `Ordenar por ${c.header}`}
                      >
                        {c.header}
                        <SetaDeOrdem dir={ativa ? sort.dir : null} />
                      </button>
                    ) : c.header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const clickable = !!onRowClick;
              return (
                <tr
                  key={rowKey ? rowKey(row) : i}
                  data-testid={testIdDe(row, i)}
                  onClick={clickable ? () => onRowClick(row) : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={clickable ? (e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(row); }
                  } : undefined}
                  className={cn(
                    "border-b border-slate-100 last:border-0",
                    clickable && "cursor-pointer hover:bg-slate-50 focus-visible:bg-brand-50 focus-visible:outline-none",
                    !clickable && "hover:bg-slate-50/60",
                    typeof rowClassName === "function" ? rowClassName(row) : rowClassName,
                  )}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn(
                        d.cell,
                        ALIGN[c.align] || ALIGN.left,
                        c.mono && "font-mono tabular-nums",
                        c.className,
                      )}
                    >
                      {c.cell ? c.cell(row) : row[c.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
      </table>
    </Card>
  );
}
